import axios from 'axios';
import { API_BASE_URL } from '../core/env.configs';
import { getJWTToken } from '../utils/helper';
import { ADMIN_DASHBOARD_CARDS } from '../utils/adminDashboardFeatures';

/**
 * Data layer for the super-admin dashboard.
 *
 * The sibling of api/b2bDashboardApi.js, but far smaller: the B2B dashboard
 * had to fan out across six legacy endpoints and reconcile them, whereas this
 * one had its aggregate endpoint built alongside it. Everything the page shows
 * comes from GET admin/v1/dashboard/summary.
 *
 * Kept behind this module anyway so the page never sees an axios response, and
 * so a later move to per-plane endpoints is a change here and nowhere else.
 */

const BASE_URL = API_BASE_URL || 'https://staging-api.shoorah.io/';

const ok = (res) => res?.data?.meta?.code === 1;
const payload = (res) => res?.data?.data;

/**
 * Whole pounds, thousands separated.
 *
 * No pence: these are platform-scale figures where the pence are noise, and a
 * card is 24px type in a 4-across grid — "£1,284,910.44" does not fit and does
 * not help.
 */
export const formatMoney = (value) => `£${Math.round(Number(value) || 0).toLocaleString()}`;

/**
 * A card's value, ready to render.
 *
 * Three states, and the difference between the last two is the point:
 *   loading      → "…"
 *   no figure    → "—"  (null from the API, or a key it does not return yet)
 *   a real value → formatted per the card's `format`
 *
 * A missing figure must never render as 0. Zero is a measurement — "nobody
 * subscribed this month" — and showing it for "we have not wired this up"
 * turns an unfinished card into a false alarm.
 */
export const cardValue = (card, stats, loading) => {
  if (loading) return '…';

  const raw = stats?.[card.key];
  if (raw === null || raw === undefined || !Number.isFinite(Number(raw))) return '—';

  return card.format === 'money' ? formatMoney(raw) : Number(raw).toLocaleString();
};

/** Cards on a plane whose figure the API did not supply — drives the band note. */
export const countPendingOnPlane = (stats, plane) =>
  ADMIN_DASHBOARD_CARDS.filter((card) => card.plane === plane).filter((card) => {
    const raw = stats?.[card.key];
    return raw === null || raw === undefined;
  }).length;

/**
 * Every figure on the dashboard.
 *
 * Resolves to `{}` rather than throwing: the dashboard renders its bands from
 * the registry, not from the response, so an outage should cost the admin the
 * numbers and not the page.
 */
export const fetchAdminDashboard = async () => {
  try {
    const res = await axios.get(`${BASE_URL}admin/v1/dashboard/summary`, {
      headers: { Authorization: getJWTToken(), deviceType: 3 }
    });
    return ok(res) ? payload(res) || {} : {};
  } catch (error) {
    console.error('Failed to load admin dashboard', error);
    return {};
  }
};

/**
 * One band's charts.
 *
 * Per band rather than all five at once: these are demographic and time-series
 * aggregations, and the cards should not wait behind them. Resolves to null on
 * failure so the band keeps its numbers and simply shows no charts.
 */
export const fetchAdminDashboardCharts = async (plane) => {
  try {
    const res = await axios.get(`${BASE_URL}admin/v1/dashboard/charts`, {
      headers: { Authorization: getJWTToken(), deviceType: 3 },
      params: { plane }
    });
    return ok(res) ? payload(res) || null : null;
  } catch (error) {
    console.error(`Failed to load ${plane} charts`, error);
    return null;
  }
};

/** Option lists for the dashboard and hub filter dialogs. */
export const fetchHubFilterData = async (params = {}) => {
  try {
    const res = await axios.get(`${BASE_URL}admin/v1/hub/filter-data`, {
      headers: { Authorization: getJWTToken(), deviceType: 3 },
      params
    });
    return ok(res) ? payload(res) : null;
  } catch (error) {
    return null;
  }
};

/** Companies as a pick-list, for hub company selectors and the report dialog. */
export const fetchCompanyOptions = async (search = '') => {
  try {
    const res = await axios.get(`${BASE_URL}admin/v1/hub/company-options`, {
      headers: { Authorization: getJWTToken(), deviceType: 3 },
      params: search ? { search } : {}
    });
    return ok(res) ? payload(res) || [] : [];
  } catch (error) {
    return [];
  }
};
